// File: packages/auth/src/provider.tsx
// Version: 2026.4.9 | ForgeTrack auth provider
//
// CHANGES in 2026.4.9:
// 1. `handleRedirectPromise()` is now AWAITED before `setReady(true)`.
//    The previous version fired-and-forgot it, which caused a race where
//    `isAuthenticated` could be read as false immediately after a redirect
//    return, before MSAL had processed the response. This is the root cause
//    of the "post-auth flicker" on the signup flow.
//
// 2. `login()` now accepts a LoginOptions object with { prompt, state }.
//    The `state` field is JSON.stringify'd and passed to MSAL's state
//    parameter, which survives the round-trip to Entra and comes back on
//    `AuthenticationResult.state`.
//
// 3. The recovered state is parsed from `handleRedirectPromise()`'s result
//    and exposed via `useAuth().redirectState`. Apps consume it once and
//    call `clearRedirectState()`.
//
// 4. Backward compat: `login('create')` still works — the bare-string arg
//    is normalized to `{ prompt: 'create' }` internally.

import { createContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus, type AccountInfo, type AuthenticationResult } from '@azure/msal-browser';
import { createMsalInstance } from './config';
import type { AuthContextType, ForgeTrackAuthConfig, LoginOptions } from './types';

export const AuthContext = createContext<AuthContextType | null>(null);

// ─────────────────────────────────────────────────────────────────────────────
// Inner context provider — lives inside <MsalProvider> so useMsal() works.
// ─────────────────────────────────────────────────────────────────────────────

function AuthContextProvider({
  config,
  initialRedirectState,
  children,
}: {
  config: ForgeTrackAuthConfig;
  initialRedirectState: unknown | null;
  children: React.ReactNode;
}) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isInitialized, setIsInitialized] = useState(false);
  const [redirectState, setRedirectState] = useState<unknown | null>(initialRedirectState);

  const account: AccountInfo | null = accounts[0] ?? null;

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      setIsInitialized(true);
    }
  }, [inProgress]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!account) return null;
    try {
      const response = await instance.acquireTokenSilent({
        scopes: config.scopes,
        account,
      });
      return response.accessToken;
    } catch {
      try {
        const response = await instance.acquireTokenPopup({ scopes: config.scopes });
        return response.accessToken;
      } catch {
        return null;
      }
    }
  }, [instance, account, config.scopes]);

  // login() accepts either a LoginOptions object OR a bare prompt string
  // (backward compat with existing call sites like `login('create')`).
  const login = useCallback(
    async (optsOrPrompt?: LoginOptions | 'create' | 'login' | 'select_account') => {
      const opts: LoginOptions =
        typeof optsOrPrompt === 'string' ? { prompt: optsOrPrompt } : (optsOrPrompt ?? {});

      await instance.loginRedirect({
        scopes: config.scopes,
        // Entra External ID (CIAM) requires prompt=create via extraQueryParameters,
        // not the MSAL `prompt` field — the OIDC prompt hint must appear as a raw
        // query param in the /authorize request for CIAM to route to sign-up.
        ...(opts.prompt ? { extraQueryParameters: { prompt: opts.prompt } } : {}),
        // `state` is an MSAL-level parameter that survives the redirect
        // round-trip and is returned on the other side. We JSON.stringify
        // whatever the caller gave us so they can pass structured payloads.
        ...(opts.state !== undefined ? { state: JSON.stringify(opts.state) } : {}),
      });
    },
    [instance, config.scopes],
  );

  const logout = useCallback(async () => {
    await instance.logoutRedirect({ postLogoutRedirectUri: config.redirectUri });
  }, [instance, config.redirectUri]);

  const clearRedirectState = useCallback(() => {
    setRedirectState(null);
  }, []);

  const tenantId = account?.tenantId ?? null;
  const isLoading = inProgress !== InteractionStatus.None;

  const value = useMemo<AuthContextType>(
    () => ({
      isInitialized,
      isLoading,
      authLoading: isLoading,
      isAuthenticated,
      account,
      accounts,
      instance,
      msalInstance: instance, // alias
      tenantId,
      inProgress,
      getAccessToken,
      login,
      logout,
      redirectState,
      clearRedirectState,
    }),
    [
      isInitialized,
      isLoading,
      isAuthenticated,
      account,
      accounts,
      instance,
      tenantId,
      inProgress,
      getAccessToken,
      login,
      logout,
      redirectState,
      clearRedirectState,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Outer provider — owns the MSAL instance and awaits handleRedirectPromise
// BEFORE rendering any children. This eliminates the race where components
// could read auth state before MSAL had processed a redirect response.
// ─────────────────────────────────────────────────────────────────────────────

export function ForgeTrackAuthProvider({
  config,
  children,
}: {
  config: ForgeTrackAuthConfig;
  children: React.ReactNode;
}) {
  const [msalInstance] = useState(() => createMsalInstance(config));
  const [ready, setReady] = useState(false);

  // Hold the parsed redirect state so we can hand it to the inner provider
  // on its first render. Using a ref avoids a re-render loop.
  const redirectStateRef = useRef<unknown | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        await msalInstance.initialize();

        // CRITICAL: await the redirect promise BEFORE marking ready.
        // If we set ready=true before this resolves, child components
        // can read isAuthenticated=false on a user who just came back
        // from a successful auth redirect — causing a flicker or, worse,
        // triggering a bounce back into the login flow.
        const result: AuthenticationResult | null = await msalInstance.handleRedirectPromise();

        // Parse and stash the state payload if MSAL gave us one.
        if (result?.state) {
          try {
            redirectStateRef.current = JSON.parse(result.state);
          } catch {
            // State wasn't JSON — expose it as-is. Callers that send
            // non-JSON state get back a string; callers that send
            // structured state get back the parsed object.
            redirectStateRef.current = result.state;
          }
        }
      } catch (err) {
        console.error('[@forgetrack/auth] MSAL init/redirect failed:', err);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [msalInstance]);

  if (!ready) return null;

  return (
    <MsalProvider instance={msalInstance}>
      <AuthContextProvider config={config} initialRedirectState={redirectStateRef.current}>
        {children}
      </AuthContextProvider>
    </MsalProvider>
  );
}
